import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ShopLayout, { fmtPrice } from '../../shop/ShopLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopList({ embedded = false }) {
  const base = embedded ? '/tienda-interna' : '/tienda';
  const [data, setData] = useState({ bikes: [], models: [], sizes: [] });
  const [loading, setLoading] = useState(true);
  const [model, setModel] = useState('');
  const [size, setSize] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sort, setSort] = useState('asc');

  useEffect(() => {
    const load = () => axios.get(`${API}/shop/public/bikes`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    load();
    const iv = setInterval(load, embedded ? 20000 : 60000);
    return () => clearInterval(iv);
  }, [embedded]);

  const bikes = useMemo(() => {
    let list = data.bikes.filter((b) => (!model || b.model === model) && (!size || b.size === size) && (!maxPrice || (b.price || 0) <= parseFloat(maxPrice)));
    list = [...list].sort((a, b) => (sort === 'asc' ? (a.price || 0) - (b.price || 0) : (b.price || 0) - (a.price || 0)));
    return list;
  }, [data, model, size, maxPrice, sort]);

  const priceCap = useMemo(() => Math.max(0, ...data.bikes.map((b) => b.price || 0)), [data]);

  return (
    <ShopLayout embedded={embedded}>
      <section className="ws-reveal" style={{ marginBottom: 48 }}>
        <p className="ws-pretitle">Bicicletas de ocasión</p>
        <h1 className="ws-title" data-testid="shop-title">Tienda Online</h1>
        <p className="ws-text" style={{ maxWidth: 640, marginTop: 20 }}>
          Bicicletas de nuestra flota, revisadas al detalle por nuestro taller en Mallorca y listas para seguir rodando contigo.
        </p>
      </section>

      <section className="ws-filters ws-reveal" style={{ marginBottom: 40 }} data-testid="shop-filters">
        <div className="ws-field">
          <label>Modelo</label>
          <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="shop-filter-model">
            <option value="">Todos</option>
            {data.models.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="ws-field">
          <label>Talla</label>
          <select value={size} onChange={(e) => setSize(e.target.value)} data-testid="shop-filter-size">
            <option value="">Todas</option>
            {data.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="ws-field">
          <label>Precio máximo {maxPrice ? `· ${fmtPrice(maxPrice)}` : ''}</label>
          <input type="range" min="0" max={priceCap || 10000} step="50" value={maxPrice || priceCap || 0} onChange={(e) => setMaxPrice(e.target.value)} data-testid="shop-filter-price" style={{ accentColor: '#FE9A25', padding: 0, border: 0, background: 'transparent' }} />
        </div>
        <div className="ws-field">
          <label>Ordenar</label>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="shop-sort">
            <option value="asc">Precio: menor → mayor</option>
            <option value="desc">Precio: mayor → menor</option>
          </select>
        </div>
        {(model || size || maxPrice) && (
          <button className="ws-btn ws-btn--ghost" style={{ padding: '10px 18px', fontSize: 12 }} onClick={() => { setModel(''); setSize(''); setMaxPrice(''); }} data-testid="shop-clear-filters">Limpiar</button>
        )}
      </section>

      {loading ? (
        <div className="ws-empty">Cargando bicicletas…</div>
      ) : bikes.length === 0 ? (
        <div className="ws-empty" data-testid="shop-empty">No hay bicicletas disponibles con estos filtros.</div>
      ) : (
        <>
          <p className="ws-muted" style={{ fontSize: 13, letterSpacing: 1, marginBottom: 20 }} data-testid="shop-count">{bikes.length} bicicleta{bikes.length === 1 ? '' : 's'}</p>
          <div className="ws-grid" data-testid="shop-grid">
            {bikes.map((b, i) => (
              <Link to={`${base}/bici/${b.id}`} key={b.id} className="ws-card ws-reveal" style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }} data-testid={`shop-card-${b.id}`}>
                <div className="ws-card-img">
                  {b.photos[0] ? <img src={b.photos[0]} alt={b.model} loading="lazy" /> : <div className="ws-noimg">Sin foto</div>}
                  <span className="ws-tag">{b.is_electric ? 'E-Bike' : 'Road'}</span>
                </div>
                <div className="ws-card-body">
                  <h2 className="ws-card-title">{b.model}</h2>
                  <div className="ws-card-meta">
                    <span>Talla {b.size}</span>
                    {b.year && <span>· {b.year}</span>}
                    <span>· Ref. {b.reference}</span>
                  </div>
                  <div className="ws-card-foot">
                    <span className="ws-price">{fmtPrice(b.price)}</span>
                    <span className="ws-link">Ver ficha <span className="ws-arrow" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </ShopLayout>
  );
}
